import { AlertTriangle, CheckCircle2, Clock, Inbox, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import type { components } from "@dolfin/api-admin";

export type DeliveryState = components["schemas"]["OutboundEInvoiceState"];

/**
 * States that change without anyone acting. Accepted is here (SDI rejects within minutes, confirms delivery within
 * up to five days) but gets its own badge, as it is no longer "sending".
 */
export const IN_FLIGHT: readonly DeliveryState[] = ["Validated", "Submitted", "Accepted"];

/** Network-agnostic superset: Peppol Submitted → Delivered; SDI Submitted → Accepted → Delivered | MadeAvailable | DeliveryFailed. */
export function DeliveryStateBadge({ state, size = "sm" }: { state?: DeliveryState; size?: "sm" | "md" }) {
  const icon = size === "md" ? "size-3.5" : "size-3";
  switch (state) {
    case "Delivered":
      return (
        <Badge variant="outline" className="gap-1 border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
          <CheckCircle2 className={icon} />
          Delivered
        </Badge>
      );
    case "MadeAvailable":
      // Not a failure: the invoice is issued. Amber because someone has to tell the customer.
      return (
        <Badge variant="outline" className="gap-1 border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
          <Inbox className={icon} />
          Issued, not delivered
        </Badge>
      );
    case "Accepted":
      return (
        <Badge variant="outline" className="gap-1 border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400">
          <Clock className={icon} />
          Awaiting network
        </Badge>
      );
    case "DeliveryFailed":
      return (
        <Badge variant="outline" className="gap-1 border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400">
          <AlertTriangle className={icon} />
          Failed
        </Badge>
      );
    case "Abandoned":
      return (
        <Badge variant="outline" className="border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400">
          Not sent
        </Badge>
      );
    // Validated and Submitted are both "we are handing it over": the difference is ours, not the user's.
    default:
      return (
        <Badge variant="outline" className="gap-1 border-blue-500/40 bg-blue-500/10 text-blue-700 dark:text-blue-400">
          <Loader2 className="size-3 animate-spin" />
          Sending
        </Badge>
      );
  }
}
